import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { REVIEW_DECISION } from '@lims/shared-types';
import {
  attemptsApi,
  resultHistoryApi,
} from '../../api/endpoints.js';
import {
  ActionButton,
  Alert,
  ErrorState,
  LoadingState,
  Panel,
  StatusBadge,
} from '../../components/ui/index.js';
import { currentLocale } from '../../i18n/index.js';
import { ReadinessBanner } from './ReadinessBanner.js';
import { ApprovalActions, CorrectionAction } from './ResultActions.js';
import {
  KeyResultsPanel,
  LockNotice,
  ResultIdentity,
  ResultRecordPanels,
  latestRevision,
} from './review-parts.js';
import { BenchLayout } from './bench-parts.js';
import { formatDateTime } from '../../components/format.js';
import { testName, toneForAttemptStatus } from './status.js';

/**
 * APPROVING ONE RESULT — module C8.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTIONS 4, 5 AND 8 BEFORE ADDING ANYTHING TO THIS PAGE.
 * ===========================================================================
 *
 * DECISION D104, WHICH GOVERNS THIS WHOLE SCREEN. Approving records that the
 * RECORD WAS CHECKED. It does not state that the material passed, conformed,
 * complied or met anything. The statement is on the page in both languages,
 * again inside the confirmation, and there is deliberately no conformity field,
 * no pass, no fail, no limit and no colour that could be read across a room as
 * a verdict on the material — the approved state renders in the NEUTRAL tone
 * for exactly that reason (see `status.ts`).
 *
 * APPROVAL IS NOT REVIEW. It is a separate permission, a separate record and a
 * separate timestamp, even where one person holds both (D97). Nobody approves
 * their own work, and the refusal is enforced by the API and by a database
 * trigger, not by the absence of a button here.
 *
 * AN APPROVED RESULT IS LOCKED, in the database. The lock is never deleted; a
 * recorded correction releases it, sends the result back and resolves only when
 * the corrected work is approved again (D101, D102).
 */
export function ApprovalPage() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locale = currentLocale();

  const history = useQuery({
    queryKey: ['result-history', attemptId],
    queryFn: () => resultHistoryApi.get(attemptId as string),
  });
  const attempt = useQuery({
    queryKey: ['attempt', attemptId],
    queryFn: () => attemptsApi.get(attemptId as string),
  });

  if (history.isLoading || attempt.isLoading) {
    return <LoadingState label={t('common.loading')} />;
  }
  if (history.error) {
    return <ErrorState error={history.error} onRetry={() => void history.refetch()} />;
  }
  if (attempt.error) {
    return <ErrorState error={attempt.error} onRetry={() => void attempt.refetch()} />;
  }
  const record = history.data;
  const work = attempt.data;
  if (!record || !work) return null;

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['result-history', attemptId] });
    await queryClient.invalidateQueries({ queryKey: ['attempt', attemptId] });
    await queryClient.invalidateQueries({ queryKey: ['approval-queue'] });
    await queryClient.invalidateQueries({ queryKey: ['review-queue'] });
    await queryClient.invalidateQueries({ queryKey: ['assignments'] });
    await queryClient.invalidateQueries({ queryKey: ['assignment', record.assignmentId] });
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const latest = latestRevision(record);
  // The technical acceptance this approval follows: the newest on the
  // revision being approved, exactly as recorded.
  const reviewed = [...record.reviewActions]
    .filter((action) => action.decision === REVIEW_DECISION.TECHNICALLY_ACCEPTED && action.revisionNumber === latest?.revisionNumber)
    .sort((left, right) => right.actedAt.localeCompare(left.actedAt))[0];

  return (
    <>
      <header className="mb-gutter flex flex-wrap items-start gap-stack">
        <ResultIdentity
          sampleId={work.assignment.sample.id}
          sampleNumber={work.assignment.sample.sampleNumber}
          pieceCode={work.assignment.pieceCode}
          testLabel={testName(work.assignment.test, locale)}
          attemptNumber={work.attemptNumber}
          performedBy={record.performedBy.displayName ?? ''}
          status={
            <StatusBadge
              label={t(`attemptStatus.${record.attemptStatus}`)}
              tone={toneForAttemptStatus(record.attemptStatus)}
            />
          }
        />
        <ActionButton priority="tertiary" onPress={() => navigate('/approval')}>
          {t('approval.backToQueue')}
        </ActionButton>
      </header>

      <div className="flex flex-col gap-gutter">
        <ReadinessBanner readiness={work.assignment.test.templateReadiness} />

        {/* D104, stated plainly and before anything else on the page. */}
        <div data-testid="approval-honesty">
          <Alert
            tone="info"
            title={t('approval.recordCheckedTitle')}
            description={t('approval.recordCheckedBody')}
          />
        </div>
        <Alert
          tone="neutral"
          title={t('approval.notDeliveryTitle')}
          description={t('approval.notDeliveryBody')}
        />

        {record.callerIsPerformer ? (
          <Alert
            tone="warning"
            title={t('approval.ownWorkTitle')}
            description={t('approval.ownWorkBody')}
          />
        ) : null}

        <LockNotice lock={record.lock} />

        <BenchLayout
          main={
            <>
              <KeyResultsPanel revision={latest} />
              <ResultRecordPanels history={record} />
            </>
          }
          side={
            <>
              <Panel title={t('bench.approvalSummary')}>
                <dl className="flex flex-col gap-2">
                  {(
                    [
                      [t('bench.fact.test'), testName(work.assignment.test, locale)],
                      [t('bench.fact.sample'), <span key="sample" dir="ltr" className="measure">{work.assignment.sample.sampleNumber}</span>],
                      [t('bench.fact.technician'), record.performedBy.displayName ?? t('common.notRecorded')],
                      [
                        t('bench.fact.reviewer'),
                        reviewed
                          ? `${reviewed.actedBy.displayName ?? ''} · ${formatDateTime(reviewed.actedAt, { locale }) ?? reviewed.actedAt}`
                          : t('common.notRecorded'),
                      ],
                      [
                        t('bench.fact.revision'),
                        latest ? t('review.revisionNumber', { number: latest.revisionNumber }) : t('common.notRecorded'),
                      ],
                    ] as const
                  ).map(([label, value]) => (
                    <div key={label} className="flex min-w-0 flex-col">
                      <dt className="text-caption text-default-600">{label}</dt>
                      <dd className="text-body text-default-900">
                        <bdi>{value}</bdi>
                      </dd>
                    </div>
                  ))}
                </dl>
              </Panel>
              <Panel title={t('bench.decisionTitle')} description={t('bench.approvalDecisionHint')}>
                <div className="flex flex-col gap-stack">
                  {record.canApprove ? (
                    <ApprovalActions history={record} onActed={refresh} />
                  ) : (
                    <Alert
                      tone="neutral"
                      title={t('approval.noActionTitle')}
                      description={t('approval.noActionBody')}
                    />
                  )}

                  {record.canCorrect ? (
                    <div className="flex flex-wrap items-center justify-end gap-inline">
                      <CorrectionAction history={record} onActed={refresh} />
                    </div>
                  ) : null}
                </div>
              </Panel>
            </>
          }
        />
      </div>
    </>
  );
}
